import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../../utils/cn';
import UserProfile from '../auth/UserProfile';
import { HelpCircle } from 'lucide-react';
import { appConfig } from '../../config/appConfig';
import { useAuth } from '../auth/AuthProvider';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { connectivity, retryConnectivityCheck, lastKnownProfileCached } = useAuth();

  const menuItems = [
    { text: 'Dashboard', icon: '📊', path: '/dashboard' },
    { text: 'Expenses', icon: '💰', path: '/expenses' },
    { text: 'Approvals', icon: '✅', path: '/approvals' },
    { text: 'Reports', icon: '📈', path: '/reports' },
    { text: 'Settings', icon: '⚙️', path: '/settings' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={cn(
        'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">EM</span>
            </div>
            <span className="text-xl font-bold text-gray-900">ExpenseManager</span>
          </div>
          <button
            onClick={handleSidebarToggle}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            ✕
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path === '/expenses' && location.pathname.startsWith('/expenses'));
              
              return (
                <li key={item.text}>
                  <button
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isActive
                        ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    )}
                  >
                    <span className="mr-3 text-lg">{item.icon}</span>
                    {item.text}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={handleSidebarToggle}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Global degraded banner */}
        {connectivity === 'degraded' && (
          <div className="sticky top-0 z-40 w-full">
            <div className="bg-amber-50 border-b border-amber-200 text-amber-900 px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
              <div className="text-sm">
                The server is currently unreachable. Some features are read-only.
                {lastKnownProfileCached && (
                  <span className="ml-2 text-amber-800 opacity-80">(Using cached profile)</span>
                )}
              </div>
              <button
                onClick={retryConnectivityCheck}
                className="text-sm px-3 py-1 rounded-md border border-amber-300 bg-white hover:bg-amber-100"
              >
                Retry now
              </button>
            </div>
          </div>
        )}
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={handleSidebarToggle}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                ☰
              </button>
              <h1 className="ml-2 text-2xl font-semibold text-gray-900 lg:ml-0">
                {menuItems.find(item => 
                  location.pathname === item.path || 
                  (item.path === '/expenses' && location.pathname.startsWith('/expenses'))
                )?.text || 'Dashboard'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Help / User Manual */}
              <button
                type="button"
                title="User Manual"
                aria-label="Open User Manual"
                onClick={() => window.open(appConfig.manualUrl, '_blank', 'noopener,noreferrer')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              <UserProfile />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <div className="p-4 sm:p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
